import { useEffect, useId, useMemo, useState } from 'react';
import axios from 'axios';

const idOf = value => value?._id || value || '';

export const goalProgress = goal => goal.status === 'completed' ? 100 : Number(goal.percent_complete || 0);

const formValues = goal => ({
  name: goal?.name || '',
  status: goal?.status || 'not-started',
  points: goal?.points ?? 0,
  percent_complete: goal ? goalProgress(goal) : 0,
  goal_type: goal?.assigned_parent_ID ? 'subgoal' : goal?.quarterNumber ? 'quarterly' : goal?.assigned_year_ID ? 'yearly' : '',
  assigned_year_ID: idOf(goal?.assigned_year_ID),
  quarterNumber: goal?.quarterNumber || '',
  assigned_parent_ID: idOf(goal?.assigned_parent_ID)
});

export default function GoalForm({ goal, allGoals, onSubmit, submitLabel = 'Save Changes', onCancel }) {
  const prefix = useId();
  const [values, setValues] = useState(() => formValues(goal));
  const [years, setYears] = useState([]);
  const [goals, setGoals] = useState(allGoals || []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [retry, setRetry] = useState(0);

  useEffect(() => { setValues(formValues(goal)); }, [goal]);
  useEffect(() => { if (allGoals) setGoals(allGoals); }, [allGoals]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    Promise.all([
      axios.post(`${process.env.REACT_APP_WEB_URL}api/year/get-user-years`, {}, { withCredentials: true }),
      allGoals ? Promise.resolve({ data: { userGoals: allGoals } }) : axios.post(`${process.env.REACT_APP_WEB_URL}api/goals/get-user-goals`, {}, { withCredentials: true })
    ]).then(([yearResponse, goalResponse]) => {
      if (cancelled) return;
      if (!Array.isArray(yearResponse.data) || !Array.isArray(goalResponse.data.userGoals)) throw new Error('Invalid options');
      setYears([...yearResponse.data].sort((a, b) => new Date(a.endDate) - new Date(b.endDate)));
      setGoals(goalResponse.data.userGoals);
    }).catch(() => {
      if (!cancelled) setLoadError('Unable to load your years and goals. Please try again.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // allGoals is kept up to date separately; refreshing a list need not reload the years.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retry]);

  const parentOptions = useMemo(() => {
    const excluded = new Set(goal?._id ? [goal._id] : []);
    let changed = true;
    while (changed) {
      changed = false;
      for (const item of goals) {
        if (excluded.has(idOf(item.assigned_parent_ID)) && !excluded.has(item._id)) {
          excluded.add(item._id);
          changed = true;
        }
      }
    }
    return goals.filter(item => !excluded.has(item._id));
  }, [goals, goal?._id]);

  const change = ({ target: { name, value } }) => {
    setValues(previous => {
      const next = { ...previous, [name]: value };
      if (name === 'status' && value === 'completed') next.percent_complete = 100;
      if (name === 'goal_type') {
        next.assigned_parent_ID = '';
        next.quarterNumber = '';
        if (!['yearly', 'quarterly'].includes(value)) next.assigned_year_ID = '';
      }
      return next;
    });
  };

  const submit = async event => {
    event.preventDefault();
    if (saving || loading || loadError) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit({
        ...values,
        assigned_year_ID: values.assigned_year_ID || null,
        assigned_parent_ID: values.assigned_parent_ID || null,
        quarterNumber: values.quarterNumber ? Number(values.quarterNumber) : null,
        points: Number(values.points || 0),
        percent_complete: values.status === 'completed' ? 100 : Number(values.percent_complete || 0)
      });
      if (!goal) setValues(formValues());
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to save the goal. Please try again.');
    } finally { setSaving(false); }
  };

  const field = (name, label, input) => <div className="form__group"><label htmlFor={`${prefix}-${name}`}>{label}</label>{input}</div>;
  const props = name => ({ id: `${prefix}-${name}`, name, value: values[name], onChange: change, className: 'pill-input' });

  return <form onSubmit={submit} className="goal-form">
    {loading && <p role="status">Loading your years and goals…</p>}
    {loadError && <p role="alert">{loadError} <button type="button" onClick={() => setRetry(n => n + 1)}>Try again</button></p>}
    <fieldset disabled={saving || loading || !!loadError}>
      {field('name', 'Name', <input {...props('name')} required placeholder="Goal name..." />)}
      {field('goal_type', 'Goal Type', <select {...props('goal_type')}>
        <option value="">No schedule</option><option value="yearly">Yearly</option><option value="quarterly">Quarterly</option><option value="subgoal">Subgoal</option>
      </select>)}
      {['yearly', 'quarterly'].includes(values.goal_type) && field('assigned_year_ID', 'Year', <select {...props('assigned_year_ID')} required>
        <option value="">Select a year</option>
        {years.map(year => <option key={year._id} value={year._id}>Year ending {new Date(year.endDate).toLocaleDateString('en-GB')}</option>)}
      </select>)}
      {values.goal_type === 'quarterly' && field('quarterNumber', 'Quarter', <select {...props('quarterNumber')} required>
        <option value="">Select a quarter</option>{[1, 2, 3, 4].map(n => <option key={n} value={n}>Quarter {n}</option>)}
      </select>)}
      {values.goal_type === 'subgoal' && field('assigned_parent_ID', 'Parent Goal', <select {...props('assigned_parent_ID')} required>
        <option value="">Select a parent goal</option>{parentOptions.map(item => <option key={item._id} value={item._id}>{item.name}</option>)}
      </select>)}
      {field('status', 'Status', <select {...props('status')}>
        <option value="not-started">Not Started</option><option value="in-progress">In Progress</option><option value="completed">Complete</option>
      </select>)}
      {field('points', 'Points', <input {...props('points')} type="number" min="0" />)}
      {field('percent_complete', 'Percent Complete', <input {...props('percent_complete')} type="number" min="0" max="100" disabled={values.status === 'completed'} />)}
      {goal && <p className="goal-form__hint">Changing the year, quarter, or parent keeps this goal’s progress, points, issues, and tasks.</p>}
      {error && <p role="alert">{error}</p>}
      <button type="submit" className="btn-solid-gray pill">{saving ? 'Saving…' : submitLabel}</button>
    </fieldset>
    {onCancel && <button type="button" className="btn-outline pill" disabled={saving} onClick={onCancel}>Cancel</button>}
  </form>;
}
